import { Context } from '../../';
import * as base from '../base';
import { NotificationPriority, NotificationType } from '../../../notifications/notifications.models';
import { PagePath } from '../../../navigation/navigation.enums';

const CONTROLLER = 'FreemiumAccount';

// DTOs that match the backend models
export interface FreemiumAccountDto {
  companyName: string;
  companySlug: string;
  companyDescription: string;
  industrySector: string;
  companySize: string;
  websiteUrls: string[];
  roleInterests: string[];
  skillInterests: string[];
  departmentInterests: string[];
  initialOnboardingComplete: boolean;
  supportingFiles: FreemiumSupportingFileDto[];
  insights: FreemiumInsightDto[];
  progress: FreemiumProgressDto;
  skillCount: number;
  roleCount: number;
  userCount: number;
}

export interface FreemiumInsightDto {
  heading: string;
  subtitle: string;
  insight: string;
}


export interface FreemiumSupportingFileDto {
  fileName: string;
  contextId: string;
  fileType: string;
}


export interface FreemiumUserDto {
  email: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  role: string;
}

export interface FreemiumProgressDto {
  hasCompanyInformation: boolean;
  hasSelectedInterests: boolean;
  hasCompanyDescription: boolean;
  hasUploadedUsers: boolean;
  hasCompletedPeerAssessment: boolean;
  percentageComplete: number;
}

// Request DTOs for API calls
export interface CreateFreemiumAccountRequest {
  email: string;
  companyName: string;
  password: string;
  confirmPassword: string;
  firstName?: string;
  lastName?: string;
}

export interface UpdateCompanyInformationRequest {
  companyName: string;
  industrySector: string;
  companySize: string;
  websiteUrls: string[];
}


export interface UpdateYourInfoRequest {
  firstName: string;
  lastName: string;
  jobTitle: string;
}

export interface UpdateInterestsRequest {
  roleInterests: string[];
  skillInterests: string[];
  departmentInterests: string[];
}

export interface UpdateCompanyDescriptionRequest {
  companyDescription: string;
  supportingFiles: FreemiumSupportingFileDto[];
}
// API Actions
export async function getFreemiumAccount(context: Context): Promise<FreemiumAccountDto | undefined> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, ''),
    method: 'GET'
  };
  const response: base.IResponse<FreemiumAccountDto> = await base.request(request);
  if (!!response.data) {
    context.state.freemiumAccount = response.data;
  }
  return response.data;
}

export async function uploadCompanyInformationFile(
  context: Context,
  payload: {
    file: File;
  }
): Promise<void> {
  const formData: FormData = new FormData();
  formData.append('File', payload.file);

  const request: base.IRequest = {
    method: 'POST',
    url: base.apiURL(CONTROLLER, `upload-file`),
    body: formData,
    authenticated: true,
    headers: { Accept: 'multipart/form-data' },
  };

  await base.request(request);
}

export async function registerFreemiumAccount(context: Context, registration: CreateFreemiumAccountRequest): Promise<{ message: string } | undefined> {
  const subdomain = 'signup';
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `register`, subdomain), authenticated: true, method: 'post', body: JSON.stringify(registration) };
  const response: base.IResponse<{ message: string }> = await base.request(request);

  if (response.error?.detail?.includes('The provided email is already in use.')) {
    context.actions.addNotification({
      id: crypto.randomUUID(),
      toastHTML: {
        __html: `Having trouble signing in? If you've already activated your account, <a href="${window.location.origin}${PagePath.accountBase}${PagePath.accountLogin}">sign in</a>. Or you can <a href="${window.location.origin}${PagePath.accountBase}${PagePath.accountForgotPassword}">reset your password</a>.`,
      },
      priority: NotificationPriority.Toast,
      type: NotificationType.Error,
      active: true,
    });
  }

  if (response.error?.type.includes('Passwords')) {
    const items = response.error.type.split('.').map(i => i.trim());
    context.state.passwordErrors = items.filter(i => !!i);
  }

  return response.data;
}


export async function updateYourInformation(context: Context, data: UpdateYourInfoRequest): Promise<void> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, 'your-info'),
    method: 'PUT',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json'
    }
  };
  await base.request(request);
}

export async function updateCompanyInformation(context: Context, data: UpdateCompanyInformationRequest): Promise<void> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, 'company-information'),
    method: 'PUT',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json'
    }
  };
  await base.request(request);
}

export async function updateInterests(context: Context, data: UpdateInterestsRequest): Promise<void> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, 'interests'),
    method: 'PUT',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json'
    }
  };
  await base.request(request);
}

export async function updateCompanyDescription(context: Context, data: UpdateCompanyDescriptionRequest): Promise<void> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, 'company-description'),
    method: 'PUT',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json'
    }
  };
  await base.request(request);
}

export function displayPaywall(context: Context, message: string): void {
  //TODO: display paywall
  console.log(message);
}

export async function completeFreemiumAssessment(): Promise<void> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, 'complete-peer-assessment'),
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  };
  await base.request(request);
}

export async function completeUserUpload(): Promise<void> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, 'complete-users-upload'),
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  };
  await base.request(request);
}

export async function tryCompleteInitialOnboarding(): Promise<void> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, 'try-complete-initial-onboarding'),
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  };
  await base.request(request);
}